import {
	DndContext,
	type DragEndEvent,
	PointerSensor,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import type { Choice } from "@memorang/types";
import { useCallback, useMemo } from "react";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../../../useAppTheme";
import { useDevice } from "../../../../useDevice";
import { AppIcon } from "../../../AppIcon";
import Box from "../../../Box";
import {
	BANK_DROPPABLE_ID,
	type MatchingPairProps,
	getChoiceById,
	resolveMatchingPairColumns,
} from "./shared";

const DRAG_HERE_PLACEHOLDER = "Drag Here";

const DraggableToken = ({
	token,
	sourceSlotId,
	disabled,
}: {
	token: Choice;
	sourceSlotId?: string;
	disabled?: boolean;
}) => {
	const theme = useAppTheme();
	const { attributes, listeners, setNodeRef, transform, isDragging } =
		useDraggable({
			id: sourceSlotId ? `slot-token:${sourceSlotId}` : `bank:${token.id}`,
			data: {
				tokenId: token.id,
				sourceSlotId,
			},
			disabled,
		});

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Translate.toString(transform),
				opacity: isDragging ? 0.5 : 1,
				cursor: disabled ? "default" : "grab",
				backgroundColor: theme.colors.surface,
				border: `1px solid ${theme.colors.outline}`,
				borderRadius: theme.roundness * 2,
				padding: "8px 16px",
				display: "flex",
				width: "100%",
				boxSizing: "border-box",
				alignItems: "center",
				justifyContent: "center",
				touchAction: "none",
			}}
			{...listeners}
			{...attributes}
		>
			<Text
				variant="bodyMedium"
				style={{
					fontSize: 16,
					lineHeight: 24,
					color: theme.colors.onSurface,
				}}
			>
				{token.text || token.id}
			</Text>
		</div>
	);
};

const GhostToken = ({ token }: { token: Choice }) => {
	const theme = useAppTheme();
	return (
		<div
			style={{
				backgroundColor: theme.colors.surfaceDisabled,
				borderRadius: theme.roundness * 2,
				padding: "8px 16px",
				display: "flex",
				width: "100%",
				boxSizing: "border-box",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Text
				variant="bodyMedium"
				style={{ fontSize: 16, lineHeight: 24, opacity: 0 }}
			>
				{token.text || token.id}
			</Text>
		</div>
	);
};

const DropSlot = ({
	slotId,
	token,
	disabled,
	showReviewState,
	isCorrect,
	onClear,
}: {
	slotId: string;
	token?: Choice;
	disabled?: boolean;
	showReviewState: boolean;
	isCorrect: boolean;
	onClear: (slotId: string) => void;
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: review-state branching
}) => {
	const theme = useAppTheme();
	const { setNodeRef, isOver } = useDroppable({
		id: `slot:${slotId}`,
		disabled,
	});

	const filled = Boolean(token);
	const canShowClose = filled && !disabled && !showReviewState;

	const reviewAnswer = isCorrect
		? theme.palette.answer.correct
		: theme.palette.answer.incorrect;

	const backgroundColor = showReviewState
		? token
			? reviewAnswer.background
			: theme.colors.surface
		: theme.colors.surface;

	const borderColor = showReviewState
		? token
			? reviewAnswer.border
			: theme.colors.outline
		: filled
			? theme.colors.primary
			: theme.colors.outline;

	const textColor =
		showReviewState && token
			? theme.colors.onSurface
			: filled
				? theme.colors.onSurface
				: theme.colors.onSurfaceVariant;

	const {
		attributes,
		listeners,
		setNodeRef: setDragRef,
		transform,
		isDragging,
	} = useDraggable({
		id: `slot-token:${slotId}`,
		data: {
			tokenId: token?.id ?? "",
			sourceSlotId: slotId,
		},
		disabled: disabled || !token || showReviewState,
	});

	return (
		<div
			ref={setNodeRef}
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: filled ? "space-between" : "flex-start",
				minHeight: 40,
				width: "100%",
				border: `1px ${filled ? "solid" : "dashed"} ${borderColor}`,
				borderRadius: theme.roundness * 2,
				backgroundColor,
				paddingLeft: 16,
				paddingRight: filled ? 8 : 16,
				paddingTop: 6,
				paddingBottom: 6,
				boxSizing: "border-box",
				overflow: "hidden",
				boxShadow: isOver ? `0 0 0 2px ${theme.colors.primary}` : "none",
			}}
		>
			{filled && token ? (
				<>
					<div
						ref={setDragRef}
						style={{
							flex: 1,
							display: "flex",
							alignItems: "center",
							cursor: disabled || showReviewState ? "default" : "grab",
							opacity: isDragging ? 0.5 : 1,
							transform: CSS.Translate.toString(transform),
						}}
						{...(disabled || showReviewState ? {} : listeners)}
						{...(disabled || showReviewState ? {} : attributes)}
					>
						<Text
							variant="bodyMedium"
							style={{ fontSize: 16, lineHeight: 24, color: textColor }}
						>
							{token.text || token.id}
						</Text>
					</div>
					{canShowClose ? (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onClear(slotId);
							}}
							onPointerDown={(e) => e.stopPropagation()}
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								background: "transparent",
								border: "none",
								margin: 0,
								width: 32,
								height: 32,
								cursor: "pointer",
								flexShrink: 0,
							}}
							aria-label="Remove match"
						>
							<AppIcon
								name="close"
								size={16}
								color={theme.colors.onSurfaceVariant}
							/>
						</button>
					) : null}
				</>
			) : (
				<Text
					variant="bodyMedium"
					style={{ fontSize: 16, lineHeight: 24, color: textColor }}
				>
					{DRAG_HERE_PLACEHOLDER}
				</Text>
			)}
		</div>
	);
};

const BankDropZone = ({
	children,
	disabled,
}: {
	children: React.ReactNode;
	disabled?: boolean;
}) => {
	const theme = useAppTheme();
	const { setNodeRef } = useDroppable({
		id: BANK_DROPPABLE_ID,
		disabled,
	});

	return (
		<div
			ref={setNodeRef}
			style={{
				display: "flex",
				flexDirection: "column",
				gap: 16,
				padding: 16,
				borderRadius: theme.roundness * 2,
				backgroundColor: theme.colors.surfaceVariant,
			}}
		>
			{children}
		</div>
	);
};

const MatchingPair = ({
	left,
	right,
	leftLabel,
	rightLabel,
	bankSide = "right",
	values,
	onChangeValues,
	disabled = false,
	isAnswered,
	inReviewMode,
	correctTokenIdBySlot,
}: MatchingPairProps) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 8 },
		}),
	);

	const { fixedItems, bankItems, fixedOnLeft } = useMemo(
		() => resolveMatchingPairColumns({ left, right, bankSide }),
		[left, right, bankSide],
	);

	const clearSlot = useCallback(
		(slotId: string) => {
			const nextValues = { ...values };
			delete nextValues[slotId];
			onChangeValues(nextValues);
		},
		[onChangeValues, values],
	);

	const assignedTokenIdSet = useMemo(
		() => new Set(Object.values(values)),
		[values],
	);

	const handleDragEnd = useCallback(
		({ active, over }: DragEndEvent) => {
			if (!over) {
				return;
			}
			const activeData = active.data.current as
				| { tokenId: string; sourceSlotId?: string }
				| undefined;
			if (!activeData) {
				return;
			}

			const nextValues = { ...values };
			const { tokenId, sourceSlotId } = activeData;

			if (over.id === BANK_DROPPABLE_ID) {
				if (sourceSlotId) {
					delete nextValues[sourceSlotId];
					onChangeValues(nextValues);
				}
				return;
			}

			if (typeof over.id === "string" && over.id.startsWith("slot:")) {
				const targetSlotId = over.id.replace("slot:", "");
				const previousSlotId = Object.entries(nextValues).find(
					([, assignedTokenId]) => assignedTokenId === tokenId,
				)?.[0];
				if (previousSlotId) {
					delete nextValues[previousSlotId];
				}
				nextValues[targetSlotId] = tokenId;
				onChangeValues(nextValues);
			}
		},
		[onChangeValues, values],
	);

	const showReviewState = Boolean(isAnswered || inReviewMode);

	const renderFixedCell = (item: Choice) => (
		<Text
			variant="bodyMedium"
			style={{ fontSize: 16, lineHeight: 24, color: theme.colors.onSurface }}
		>
			{item.text || item.id}
		</Text>
	);

	const renderSlotCell = (slotId: string) => {
		const assignedTokenId = values[slotId];
		const assignedToken = getChoiceById(bankItems, assignedTokenId);
		return (
			<DropSlot
				slotId={slotId}
				token={assignedToken}
				disabled={disabled}
				showReviewState={showReviewState}
				isCorrect={
					assignedTokenId != null &&
					correctTokenIdBySlot?.[slotId] === assignedTokenId
				}
				onClear={clearSlot}
			/>
		);
	};

	return (
		<DndContext sensors={sensors} onDragEnd={handleDragEnd}>
			<Box
				gap={16}
				flexDirection={isMobile ? "column" : "row"}
				alignItems="flex-start"
				paddingHorizontal={isMobile ? 16 : 0}
			>
				<Box flex={1} gap={0} style={{ width: "100%" }}>
					<Box
						flexDirection="row"
						alignItems="center"
						paddingBottom={8}
						gap={16}
					>
						<Box flex={1}>
							<Text
								variant="titleSmall"
								style={{ color: theme.colors.primary, fontWeight: "700" }}
							>
								{leftLabel ?? ""}
							</Text>
						</Box>
						<Box flex={1}>
							<Text
								variant="titleSmall"
								style={{ color: theme.colors.primary, fontWeight: "700" }}
							>
								{rightLabel ?? ""}
							</Text>
						</Box>
					</Box>

					{fixedItems.map((item) => (
						<Box
							key={item.id}
							flexDirection="row"
							alignItems="center"
							gap={16}
							paddingVertical={8}
							style={{
								borderTopWidth: 1,
								borderTopColor: theme.colors.surfaceVariant,
								borderStyle: "dashed",
							}}
						>
							<Box flex={1}>
								{fixedOnLeft ? renderFixedCell(item) : renderSlotCell(item.id)}
							</Box>
							<Box flex={1}>
								{fixedOnLeft ? renderSlotCell(item.id) : renderFixedCell(item)}
							</Box>
						</Box>
					))}
				</Box>

				<Box style={{ width: isMobile ? "100%" : 220 }}>
					<BankDropZone disabled={disabled}>
						{bankItems.map((token) => {
							if (assignedTokenIdSet.has(token.id)) {
								return <GhostToken key={token.id} token={token} />;
							}
							return (
								<DraggableToken
									key={token.id}
									token={token}
									disabled={disabled || showReviewState}
								/>
							);
						})}
					</BankDropZone>
				</Box>
			</Box>
		</DndContext>
	);
};

export default MatchingPair;
